import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';

/**
 * Bugsnag frontend bootstrap. Called once from main.tsx before React mounts.
 *
 * Env wiring:
 *   - VITE_BUGSNAG_API_KEY        required to activate; empty -> no-op
 *   - VITE_BUGSNAG_RELEASE_STAGE  e.g. "production" | "staging"; defaults
 *                                 to import.meta.env.MODE
 *   - VITE_BUGSNAG_APP_VERSION    typically the Git SHA from CI
 *
 * Bugsnag's free tier is 7500 events/month — comfortable headroom for a
 * low-traffic site during catalog bootstrap. No spike protection magic
 * that drops events silently the way Sentry's free tier did.
 *
 * No-op when the API key is empty so dev (`VITE_USE_MOCKS=true`) and
 * tests (key unset in `src/test/setup.ts`) never ship events.
 *
 * Returns the React ErrorBoundary component for the caller to wrap App
 * with. When Bugsnag isn't initialized (no key), returns a passthrough
 * component so main.tsx doesn't need a branching mount path.
 */
export function initBugsnag(): React.ComponentType<{
  FallbackComponent?: React.ComponentType<{ error: Error; clearError: () => void }>;
  children?: React.ReactNode;
}> {
  const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY as string | undefined;
  if (!apiKey) {
    // No-op passthrough boundary — same component contract as the real
    // one so main.tsx can render `<Boundary fallback={...}>...</Boundary>`
    // regardless of whether Bugsnag is active.
    return ({ children }: { children?: React.ReactNode }) =>
      (children as React.ReactElement) ?? null;
  }

  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    releaseStage:
      (import.meta.env.VITE_BUGSNAG_RELEASE_STAGE as string | undefined) ||
      import.meta.env.MODE,
    appVersion:
      (import.meta.env.VITE_BUGSNAG_APP_VERSION as string | undefined) || undefined,
    // Filter out noisy non-actionable errors before they leave the
    // browser. Add more here as patterns emerge.
    onError: (event) => {
      const ignored = [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ];
      const msg = event.errors?.[0]?.errorMessage ?? '';
      if (ignored.some((pat) => msg.includes(pat))) return false;
      return true;
    },
  });

  const reactPlugin = Bugsnag.getPlugin('react');
  if (!reactPlugin) {
    // Defensive — should never hit since we registered the plugin above.
    return ({ children }: { children?: React.ReactNode }) =>
      (children as React.ReactElement) ?? null;
  }
  return reactPlugin.createErrorBoundary(React) as React.ComponentType<{
    FallbackComponent?: React.ComponentType<{ error: Error; clearError: () => void }>;
    children?: React.ReactNode;
  }>;
}
